import { FC, useContext, useEffect, useState } from 'react';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { RouteType } from '../../../@types/RouteType';
import { NavContext } from '../../../context/NavContext';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useAuth from '../../../hooks/useAuth';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import useCartOrderContext from '../../../hooks/useCartOrder';

interface NavItemProps {
  item: RouteType,
  isChild?: boolean,
}

const NavItem: FC<NavItemProps> = ({ item, isChild }) => {
  const [open, setOpen] = useState<boolean>(false);
  const navContextData = useContext(NavContext);
  const [selected, setSelected] = useState<boolean>(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const { logout } = useAuth();
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));
  const { setInitialOrder } = useCartOrderContext();

  const handleLinkClicked = (item: RouteType) => {
    // Handle special usecase for logout nav button
    if (item.state === 'logout') logout();

    // Toggle open and close of drop down if
    // the nav is expanded and is not mobile
    setOpen(!navContextData?.navExpanded && !isTablet
      ? false : !open);

    // Close the nav drawer on mobile when a link is clicked
    if (isTablet && !item.child?.length) {
      navContextData?.setNavExpanded(false)
    };

    // Check current route state for a match
    // when matched there is no need to navigate 
    // else navigate
    if (item.externalPath) {
      window.open(item.path, '_blank');
    } else {
      if (navContextData?.currentRoute?.state !== item.state) {
        /*
          Check if item has children then navigate to the first child
        */
        if (item.child) {
          /*
            Check for index if exists and route to the index based on the 
            index state else just use path
          */
          const firstChild = item.child[0];
          if (firstChild.index) {
            /*
              Convert index string into a path
            */
            navigate(firstChild.state.replace('.', '/'));
          } else {
            navigate(firstChild.path ?? '');
          }
        } else {
          navigate(item.path ?? '')
        }
      };

      // Set route display text and route state to context
      navContextData?.setRouteDisplayText(item.displayText);
      navContextData?.setCurrentRoute(item);
    }

    /*
      If New Cart Order reset initialCartOrderState
    */
    if (item.state === 'orders.carts.new-order')
      setInitialOrder(undefined);
  }

  useEffect(() => {
    // Close open drop down on a nav item
    // if the nav changes from expanded to collapsed
    // only if not on mobile
    if (!navContextData?.navExpanded && !isTablet) setOpen(false);

    // Close the drop down on nav item if
    // another drop down is expanded
    if (navContextData?.navExpanded) {
      const baseState = navContextData.currentRoute?.state.split('.')[0];
      if (baseState && !item.state.startsWith(baseState)) setOpen(false);
    }
    
    setSelected(navContextData?.currentRoute?.state.split('.')[0] === item.state
      || navContextData?.currentRoute?.state === item.state)
    setOpen(navContextData?.navExpanded && navContextData?.currentRoute?.state.split('.')[0] === item.state
      || navContextData?.currentRoute?.state === item.state)
  }, [navContextData, isTablet, setOpen]);

  return (
    item.sidebarProps && item.path ? (
      <Stack>
        <Tooltip
          title={!navContextData?.navExpanded ? item.displayText : ''}
          placement="right"
          disableInteractive>
          <ListItemButton
            id={item.sidebarProps.id}
            onClick={() => handleLinkClicked(item)}
            selected={selected}
            sx={{
              ...(!isChild) && {
                "&.Mui-selected, &.Mui-selected:hover, &.Mui-focusVisible": {
                  backgroundImage: "linear-gradient(to right, #931216, #E20B23, #FF8230)"
                },
              },
              paddingY: "12px",
              paddingX: "20px"
            }}>
            {isChild &&
              <ListItemIcon
                sx={{
                  color: '#cb2a20',
                  justifyContent: 'flex-end',
                  paddingRight: '8px'
                }}>
                {selected && <ArrowRightIcon />}
              </ListItemIcon>
            }
            {!isChild &&
              <ListItemIcon
                sx={{ color: "white" }}>
                {item.sidebarProps.icon}
              </ListItemIcon>
            }
            <ListItemText>
              <Typography variant='nav'>{item.sidebarProps.displayText}</Typography>
            </ListItemText>
            {item.child &&
              (!isTablet ?
                (item.child.some((child) => child.sidebarProps && !child.sidebarProps?.mobileOnly) &&
                  (open ? <ExpandLessIcon /> : <ExpandMoreIcon />)
                ) :
                (item.child.some((child) => child.sidebarProps) &&
                  (open ? <ExpandLessIcon /> : <ExpandMoreIcon />)
                )
              )
            }
          </ListItemButton>
        </Tooltip>
        {item.child &&
          (!isTablet ?
            (item.child.some((child) => child.sidebarProps && !child.sidebarProps?.mobileOnly) &&
              <Collapse in={open} timeout="auto">
                <List>
                  {item.child?.map((route, index) => (
                    route.sidebarProps ? (
                      route.sidebarProps.mobileOnly ? null
                        : <NavItem isChild={true} key={index} item={route} />
                    ) : null
                  ))}
                </List>
              </Collapse>
            ) :
            (
              <Collapse in={open} timeout="auto">
                <List>
                  {item.child?.map((route, index) => (
                    route.sidebarProps ? (<NavItem isChild={true} key={index} item={route} />) : null
                  ))}
                </List>
              </Collapse>
            )
          )
        }
      </Stack>
    ) : null
  )
};

export default NavItem;
